import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

interface UseDynamicTextWidthParams {
  enabled: boolean;
  value: unknown;
  minWidth: number;
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  buffer?: number;
}

export const useDynamicTextWidth = ({
  enabled,
  value,
  minWidth,
  inputRef,
  buffer = 0,
}: UseDynamicTextWidthParams) => {
  const sizerRef = useRef<HTMLSpanElement | null>(null);
  const normalizedValue = useMemo(
    () =>
      typeof value === "string" || typeof value === "number" ? `${value}` : "",
    [value],
  );

  const [dynamicWidth, setDynamicWidth] = useState(() => {
    if (!enabled) return minWidth;
    const longestLine = normalizedValue
      .split("\n")
      .reduce((max, line) => (line.length > max.length ? line : max), "");
    const estimatedWidth = longestLine.length * 8 + 16 + buffer;
    return Math.max(minWidth, estimatedWidth);
  });

  const updateDynamicWidth = useCallback(
    (
      nextValue: string,
      inputElement?: HTMLInputElement | HTMLTextAreaElement | null,
    ): boolean => {
      if (!enabled) return false;

      const sizerElement = sizerRef.current;
      const element = inputElement ?? inputRef.current;
      if (!element || !sizerElement) return false;

      const styles = window.getComputedStyle(element);
      const longestLine = nextValue
        .split("\n")
        .reduce((max, line) => (line.length > max.length ? line : max), "");

      sizerElement.style.font = styles.font;
      sizerElement.style.letterSpacing = styles.letterSpacing;
      sizerElement.style.textTransform = styles.textTransform;
      sizerElement.textContent = longestLine || " ";

      const measuredTextWidth = Math.ceil(
        sizerElement.getBoundingClientRect().width,
      );
      const horizontalPadding =
        (parseFloat(styles.paddingLeft) || 0) +
        (parseFloat(styles.paddingRight) || 0);
      const horizontalBorders =
        (parseFloat(styles.borderLeftWidth) || 0) +
        (parseFloat(styles.borderRightWidth) || 0);

      const nextWidth = Math.max(
        minWidth,
        measuredTextWidth + horizontalPadding + horizontalBorders + buffer,
      );
      setDynamicWidth(prev => (prev === nextWidth ? prev : nextWidth));
      return true;
    },
    [buffer, enabled, inputRef, minWidth],
  );

  useLayoutEffect(() => {
    if (!enabled) {
      setDynamicWidth(prev => (prev === minWidth ? prev : minWidth));
      return;
    }

    const measured = updateDynamicWidth(normalizedValue);
    if (!measured) {
      const longestLine = normalizedValue
        .split("\n")
        .reduce((max, line) => (line.length > max.length ? line : max), "");
      const estimatedWidth = longestLine.length * 8 + 16 + buffer;
      const nextWidth = Math.max(minWidth, estimatedWidth);
      setDynamicWidth(prev => (prev === nextWidth ? prev : nextWidth));
    }
  }, [buffer, enabled, minWidth, normalizedValue, updateDynamicWidth]);

  return {
    dynamicWidth,
    sizerRef,
    updateDynamicWidth,
  };
};
